import {useMemo} from "react";
import EChartsReact from "echarts-for-react";
import InputGroup from "./InputGroup";
import {distributionBarOption, pieOption} from "../../constants/chart_config";
import {toRespondentAgeSeries, toRespondentGenderData} from "../../libs/surveyDemographics";

/**
 * '설문조사 분석' 탭 맨 위 — **설문 응답자의 성별·연령대**. (2026-10-06)
 *
 * '고객 분석' 탭(`SpecificCustomerStatistics`)의 성별 원형·연령대 막대와 **같은 카드·같은 차트 옵션**을
 * 한 줄(2열)로 놓는다. 모집단만 다르다 — 이 프로젝트 참여 고객 전체가 아니라 **설문에 응답한 고객**이다.
 * 판정·분모 규칙은 `libs/surveyDemographics.js`.
 *
 * @param rows 응답자별 시트의 행(`gender`, `ageGroup` 포함)
 */
const SurveyRespondentDemographics = ({rows}) => {
    const genderData = useMemo(() => toRespondentGenderData(rows), [rows]);
    const ageSeries = useMemo(() => toRespondentAgeSeries(rows), [rows]);
    const collectedGender = genderData.reduce((sum, item) => sum + item.count, 0);
    const collectedAge = (ageSeries.counts ?? []).reduce((sum, count) => sum + (count ?? 0), 0);

    return (
        <InputGroup
            label="응답자 성별·연령대"
            description={`설문 응답자 ${rows.length.toLocaleString()}명 기준 · '고객 분석' 과 같은 기준이며 정보가 수집되지 않은 고객은 비율에서 제외됩니다.`}
        >
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded border border-gray-300">
                    <div className="border-b border-b-gray-300 w-full p-4 flex justify-between items-baseline">
                        <h5 className="font-semibold">성별</h5>
                        <span className="text-xs text-gray-400">수집 {collectedGender.toLocaleString()}명</span>
                    </div>
                    <div className="p-4">
                        {collectedGender > 0
                            ? <EChartsReact option={pieOption("성별", genderData, "%")} />
                            : <div className="py-16 text-center text-sm text-gray-400">수집된 성별 정보가 없습니다.</div>}
                    </div>
                </div>
                <div className="bg-white rounded border border-gray-300">
                    <div className="border-b border-b-gray-300 w-full p-4 flex justify-between items-baseline">
                        <h5 className="font-semibold">연령대</h5>
                        <span className="text-xs text-gray-400">수집 {collectedAge.toLocaleString()}명</span>
                    </div>
                    <div className="p-4">
                        {collectedAge > 0
                            ? <EChartsReact option={distributionBarOption(ageSeries.xAxis, ageSeries.series, ageSeries.counts)} />
                            : <div className="py-16 text-center text-sm text-gray-400">수집된 연령 정보가 없습니다.</div>}
                    </div>
                </div>
            </div>
        </InputGroup>
    );
};

export default SurveyRespondentDemographics;
